import { useEffect } from "react";

const DEFAULT_TITLE = "Plainly · Analytics, in plain English";
const DEFAULT_DESC =
  "Plainly reads your Google Analytics and tells you in plain English what changed this week, why it happened, and what to try next. AI-powered analytics for non-technical business owners.";
const DEFAULT_OG_IMAGE = "https://myplainly.com/brand/plainly-og-image.png";

/**
 * Lightweight per-page SEO hook.
 * Sets <title>, description, canonical, og:* and twitter:* per route.
 *
 * For blog/article pages, pass `article` with publishedTime + author so we
 * emit og:type="article" + article:published_time + article:author, which
 * Facebook, LinkedIn, Slack and iMessage all use for rich previews.
 */
export function usePageMeta({
  title,
  description,
  path,
  ogType,            // "website" | "article" — defaults to "website" (or "article" if `article` is set)
  ogImage,           // override image for this page
  keywords,          // comma-separated or array of keyword strings
  article,           // { publishedTime?, modifiedTime?, author?, section?, tags? }
}) {
  useEffect(() => {
    const fullTitle = title ? `${title} · Plainly` : DEFAULT_TITLE;
    document.title = fullTitle;

    const desc = description || DEFAULT_DESC;
    const url = path ? `https://myplainly.com${path}` : "https://myplainly.com";
    const img = ogImage || DEFAULT_OG_IMAGE;
    const type = ogType || (article ? "article" : "website");

    const upsertMeta = (key, attr, value) => {
      if (value == null) return;
      const selector = attr === "property"
        ? `meta[property="${key}"]`
        : `meta[name="${key}"]`;
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    const upsertLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    // Core
    upsertMeta("description", "name", desc);
    upsertLink("canonical", url);

    // Keywords (legacy but still parsed by Bing, DuckDuckGo, and some AI search engines)
    if (keywords) {
      const kwStr = Array.isArray(keywords) ? keywords.join(", ") : String(keywords);
      upsertMeta("keywords", "name", kwStr);
    }

    // OpenGraph
    upsertMeta("og:title", "property", fullTitle);
    upsertMeta("og:description", "property", desc);
    upsertMeta("og:url", "property", url);
    upsertMeta("og:type", "property", type);
    upsertMeta("og:image", "property", img);
    upsertMeta("og:image:width", "property", "1200");
    upsertMeta("og:image:height", "property", "630");

    // Twitter
    upsertMeta("twitter:card", "name", "summary_large_image");
    upsertMeta("twitter:title", "name", fullTitle);
    upsertMeta("twitter:description", "name", desc);
    upsertMeta("twitter:image", "name", img);

    // Article-specific (Facebook/LinkedIn/Slack rich previews)
    if (article) {
      if (article.publishedTime) upsertMeta("article:published_time", "property", article.publishedTime);
      if (article.modifiedTime) upsertMeta("article:modified_time", "property", article.modifiedTime);
      if (article.author) upsertMeta("article:author", "property", article.author);
      if (article.section) upsertMeta("article:section", "property", article.section);
      if (Array.isArray(article.tags)) {
        // Remove old tag tags, then add fresh ones
        document.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
        article.tags.forEach((t) => {
          const el = document.createElement("meta");
          el.setAttribute("property", "article:tag");
          el.setAttribute("content", t);
          document.head.appendChild(el);
        });
      }
    } else {
      // Clean up leftover article tags when navigating away from a blog post
      ["article:published_time", "article:modified_time", "article:author", "article:section"].forEach((key) => {
        const n = document.querySelector(`meta[property="${key}"]`);
        if (n) n.remove();
      });
      document.querySelectorAll('meta[property="article:tag"]').forEach((n) => n.remove());
    }
  }, [title, description, path, ogType, ogImage, keywords, article]);
}
